import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

const STATUS_MAP = {
  completed: '完成', failed: '失败', pending: '等待', processing: '处理中', expired: '过期'
};

function AdminUserDetail() {
  const { id } = useParams();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        const result = await res.json();
        if (result.success) setData(result.data);
        else setError(result.error);
      } catch (e) { setError('加载失败'); }
      finally { setLoading(false); }
    };
    if (session) fetchData();
  }, [session, id]);

  if (loading) return <div className="ad-loading">加载中...</div>;
  if (error) return <div className="ad-error">{error}</div>;
  if (!data) return null;

  const { user, tasks, transactions, payments } = data;
  const fmtMoney = (n) => `¥${(parseFloat(n) || 0).toFixed(2)}`;
  const fmtTime = (t) => new Date(t).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  const fmtDate = (t) => new Date(t).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <button className="ad-nav-btn" onClick={() => navigate('/admin/users')} style={{ marginBottom: 16 }}>← 返回用户列表</button>

      {/* 用户信息卡片 */}
      <div className="ad-term">
        <div className="ad-term-bar">
          <div className="ad-term-dots">
            <span className="ad-term-dot ad-term-dot--red" />
            <span className="ad-term-dot ad-term-dot--yellow" />
            <span className="ad-term-dot ad-term-dot--green" />
          </div>
          <span className="ad-term-title">user_profile</span>
        </div>
        <div className="ad-term-body">
          <div className="ad-info-grid">
            <div className="ad-info-item">
              <span className="ad-info-label">邮箱</span>
              <span className="ad-info-value">{user.email}</span>
            </div>
            <div className="ad-info-item">
              <span className="ad-info-label">ID</span>
              <span className="ad-info-value" style={{ fontSize: 11 }}>{user.id}</span>
            </div>
            <div className="ad-info-item">
              <span className="ad-info-label">余额</span>
              <span className="ad-info-value">{fmtMoney(user.balance)}</span>
            </div>
            <div className="ad-info-item">
              <span className="ad-info-label">冻结余额</span>
              <span className="ad-info-value">{fmtMoney(user.frozen_balance)}</span>
            </div>
            <div className="ad-info-item">
              <span className="ad-info-label">注册时间</span>
              <span className="ad-info-value">{fmtDate(user.created_at)}</span>
            </div>
            <div className="ad-info-item">
              <span className="ad-info-label">邮箱验证</span>
              <span className="ad-info-value">{user.email_confirmed_at ? '已验证' : '未验证'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 近期任务 */}
      <div className="ad-term">
        <div className="ad-term-bar">
          <div className="ad-term-dots">
            <span className="ad-term-dot ad-term-dot--red" />
            <span className="ad-term-dot ad-term-dot--yellow" />
            <span className="ad-term-dot ad-term-dot--green" />
          </div>
          <span className="ad-term-title">recent_tasks — {tasks.length} 条</span>
        </div>
        <div className="ad-term-body">
          {tasks.length === 0 ? <div className="ad-empty">暂无任务</div> : (
            <div className="ad-table-wrap">
              <table className="ad-table">
                <thead>
                  <tr><th>时间</th><th>类型</th><th>状态</th><th>费用</th><th>语言</th></tr>
                </thead>
                <tbody>
                  {tasks.map(t => (
                    <tr key={t.id}>
                      <td>{fmtTime(t.created_at)}</td>
                      <td>{t.task_type}</td>
                      <td><span className={`ad-badge ad-badge--${t.status}`}>{STATUS_MAP[t.status] || t.status}</span></td>
                      <td>{t.cost != null ? fmtMoney(parseFloat(t.cost)) : '-'}</td>
                      <td>{t.source_language}{t.target_language ? ` → ${t.target_language}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 近期交易 */}
      <div className="ad-term">
        <div className="ad-term-bar">
          <div className="ad-term-dots">
            <span className="ad-term-dot ad-term-dot--red" />
            <span className="ad-term-dot ad-term-dot--yellow" />
            <span className="ad-term-dot ad-term-dot--green" />
          </div>
          <span className="ad-term-title">recent_transactions — {transactions.length} 条</span>
        </div>
        <div className="ad-term-body">
          {transactions.length === 0 ? <div className="ad-empty">暂无交易</div> : (
            <div className="ad-table-wrap">
              <table className="ad-table">
                <thead>
                  <tr><th>时间</th><th>类型</th><th>金额</th><th>余额变动</th><th>描述</th></tr>
                </thead>
                <tbody>
                  {transactions.map(t => (
                    <tr key={t.id}>
                      <td>{fmtTime(t.created_at)}</td>
                      <td>{t.type}</td>
                      <td style={{ color: parseFloat(t.amount) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {parseFloat(t.amount) >= 0 ? '+' : ''}{fmtMoney(parseFloat(t.amount))}
                      </td>
                      <td>{t.balance_after != null ? fmtMoney(parseFloat(t.balance_after)) : '-'}</td>
                      <td style={{ color: 'var(--text-3)' }}>{t.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 近期支付 */}
      <div className="ad-term">
        <div className="ad-term-bar">
          <div className="ad-term-dots">
            <span className="ad-term-dot ad-term-dot--red" />
            <span className="ad-term-dot ad-term-dot--yellow" />
            <span className="ad-term-dot ad-term-dot--green" />
          </div>
          <span className="ad-term-title">recent_payments — {payments.length} 条</span>
        </div>
        <div className="ad-term-body">
          {payments.length === 0 ? <div className="ad-empty">暂无支付</div> : (
            <div className="ad-table-wrap">
              <table className="ad-table">
                <thead>
                  <tr><th>时间</th><th>金额</th><th>方式</th><th>状态</th></tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id}>
                      <td>{fmtTime(p.created_at)}</td>
                      <td>{fmtMoney(parseFloat(p.amount))}</td>
                      <td>{p.payment_method || '-'}</td>
                      <td><span className={`ad-badge ad-badge--${p.status}`}>{STATUS_MAP[p.status] || p.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminUserDetail;
